<script setup>
/**
 * One word for where a paid send has got to.
 *
 * THE COLOUR RULE HERE IS NOT THE USUAL ONE. Everywhere else in this section the accent
 * means "fine". On a broadcast it means "money is leaving right now", which is the state
 * an owner most needs to spot on a list — so `running` takes the accent and `completed`,
 * which is genuinely fine and needs nothing from her, takes a quiet slate.
 *
 * `unknown` is the seventh status and it is ours, not the sidecar's (`api/client.js`): a
 * status added upstream and missing from this service's hand-written mirror reaches the
 * browser unvalidated, and a chip that renders it blank — which is what an unmapped lookup
 * does, since Vue prints `undefined` as nothing — would leave the owner with no word at
 * all for a send that may be costing her money. It gets a real word and an amber that says
 * "look at this", because that is exactly what it means.
 */
import { computed } from 'vue';

import { useWasloI18n } from '../../i18n.js';

const props = defineProps({
  status: { type: String, required: true },
});

const { t } = useWasloI18n();

const TONE = {
  draft: 'bg-n-alpha-2 text-n-slate-11',
  running: 'bg-n-blue-3 text-n-blue-11',
  paused: 'bg-n-amber-3 text-n-amber-11',
  completed: 'bg-n-slate-3 text-n-slate-11',
  cancelled: 'bg-n-slate-3 text-n-slate-11',
  failed: 'bg-n-ruby-3 text-n-ruby-11',
  unknown: 'bg-n-amber-3 text-n-amber-11',
};

const KEY = {
  draft: 'DRAFT',
  running: 'RUNNING',
  paused: 'PAUSED',
  completed: 'COMPLETED',
  cancelled: 'CANCELLED',
  failed: 'FAILED',
  unknown: 'UNKNOWN',
};

const tone = computed(() => TONE[props.status] ?? TONE.unknown);
const label = computed(() =>
  t(`WASLO.BROADCASTS.STATUS.${KEY[props.status] ?? 'UNKNOWN'}`)
);
</script>

<template>
  <span
    class="inline-flex items-center h-6 px-2 text-xs font-medium rounded-md shrink-0"
    :class="tone"
    :data-waslo-broadcast-status="status"
  >
    {{ label }}
  </span>
</template>
